
/**
 * TiledWindow - Individual tiled window component
 * 
 * A draggable, resizable, closable window that displays entity content.
 * Supports minimize/maximize states and focus management.
 * 
 * Positioned absolutely within its container (not fixed to viewport).
 */

import { type ReactNode, type RefObject } from 'react';
import { motion } from 'framer-motion';
import { 
    X, 
    Workflow as WorkflowIcon,
    Bot,
    Wrench,
    MessageSquare,
    Brain,
    Sparkles,
    Shield,
    FileCode,
    UserCog,
    Scale,
    Loader2,
    AlertTriangle,
    ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { TiledWindow as TiledWindowType, TiledWindowEntityType, WindowState, WindowGeometry } from '@/types/tiled-window';
import { capabilityColors } from '@/lib/capability-colors';

/** Icon configuration for each entity type */
const entityIcons: Record<TiledWindowEntityType, { icon: typeof WorkflowIcon; color: string; bgColor: string }> = {
    workflow: { 
        icon: WorkflowIcon, 
        color: capabilityColors.workflow.text, 
        bgColor: capabilityColors.workflow.bgLight,
    },
    agent: { 
        icon: Bot, 
        color: capabilityColors.agent.text, 
        bgColor: capabilityColors.agent.bgLight,
    },
    tool: { 
        icon: Wrench, 
        color: capabilityColors.tool.text, 
        bgColor: capabilityColors.tool.bgLight,
    },
    prompt: { 
        icon: MessageSquare, 
        color: 'text-purple-600', 
        bgColor: 'bg-purple-100',
    },
    model: { 
        icon: Brain, 
        color: capabilityColors.model.text, 
        bgColor: capabilityColors.model.bgLight,
    },
    skill: { 
        icon: Sparkles, 
        color: 'text-amber-600', 
        bgColor: 'bg-amber-100',
    },
    role: { 
        icon: UserCog, 
        color: 'text-cyan-600', 
        bgColor: 'bg-cyan-100',
    },
    policy: { 
        icon: Shield, 
        color: 'text-rose-600', 
        bgColor: 'bg-rose-100',
    },
    schema: { 
        icon: FileCode, 
        color: 'text-slate-600', 
        bgColor: 'bg-slate-100',
    },
    decision: {
        icon: Scale,
        color: capabilityColors.decision.text,
        bgColor: capabilityColors.decision.bgLight,
    },
};

interface TiledWindowProps {
    window: TiledWindowType;
    isFocused: boolean;
    onFocus: () => void;
    onClose: () => void;
    onGeometryChange: (geometry: Partial<WindowGeometry>) => void;
    onStateChange: (state: WindowState) => void;
    /** Render the window content */
    renderContent: (window: TiledWindowType) => ReactNode;
    /** Optional: Open in external page handler */
    onOpenExternal?: (window: TiledWindowType) => void;
    /** Container ref for calculating bounds and constraining window position */
    containerRef?: RefObject<HTMLDivElement | null>;
}

export function TiledWindow({
    window: windowData,
    isFocused,
    onFocus,
    onClose,
    renderContent,
    onOpenExternal,
}: TiledWindowProps) {
    const { title, entityType, isLoading, error } = windowData;
    const iconConfig = entityIcons[entityType];
    const IconComponent = iconConfig.icon;

    // Windows are always maximized - fill the container
    const windowStyle = { 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%',
        zIndex: windowData.zIndex,
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ 
                type: 'spring',
                damping: 20,
                stiffness: 150,
            }}
            className={cn(
                'pointer-events-auto absolute flex flex-col overflow-hidden rounded-lg border bg-white shadow-2xl',
                isFocused ? 'border-blue-400 ring-2 ring-blue-200' : 'border-slate-200',
            )}
            style={windowStyle}
            onMouseDown={() => onFocus()}
        >
            {/* Title Bar */}
            <div
                className={cn(
                    'flex h-10 shrink-0 items-center gap-2 border-b px-3',
                    isFocused ? 'bg-slate-100' : 'bg-slate-50',
                )}
            >
                {/* Entity Icon */}
                <div className={cn('flex h-6 w-6 items-center justify-center rounded', iconConfig.bgColor)}>
                    <IconComponent size={14} className={iconConfig.color} />
                </div>

                {/* Title */}
                <div className="flex flex-1 items-center gap-2 overflow-hidden">
                    <span className="truncate text-sm font-medium text-slate-700">
                        {title}
                    </span>
                    <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-500">
                        {entityType}
                    </span>
                </div>

                {/* Window Controls */}
                <div className="flex items-center gap-1">
                    {/* Open External */}
                    {onOpenExternal && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                            onClick={() => onOpenExternal(windowData)}
                            title="Open in new tab"
                        >
                            <ExternalLink size={12} />
                        </Button>
                    )}
                    {/* Close */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-400 hover:bg-red-100 hover:text-red-600"
                        onClick={onClose}
                        title="Close"
                    >
                        <X size={12} />
                    </Button>
                </div>
            </div>

            {/* Content Area */}
            <div className="min-h-0 flex-1 overflow-hidden bg-white">
                {isLoading ? (
                    <div className="flex h-full items-center justify-center">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                            <span className="text-sm text-slate-500">Loading {entityType}...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="flex h-full items-center justify-center p-4">
                        <div className="flex flex-col items-center gap-3 text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900">Failed to load {entityType}</p>
                                <p className="mt-1 text-sm text-slate-500">{error}</p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onClose}
                            >
                                Close Window
                            </Button>
                        </div>
                    </div>
                ) : (
                    renderContent(windowData)
                )}
            </div>
        </motion.div>
    );
}

